import { useEffect, useRef, useState } from "react";
import api, { fmtINR, formatApiError, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Package, X, Loader2, ImagePlus } from "lucide-react";

const EMPTY = {
  name: "",
  sku: "",
  category: "figures",
  price: "",
  stock: "",
  description: "",
  image_url: "",
};

const CATEGORIES = ["figures", "manga", "posters", "apparel", "keychains", "cards", "misc"];

export default function Products() {
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/api/products");
    setProducts(data);
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name,
      sku: p.sku,
      category: p.category,
      price: p.price,
      stock: p.stock,
      description: p.description || "",
      image_url: p.image_url || "",
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        ...form,
        sku: (form.sku || "").trim().toUpperCase(),
        price: parseFloat(form.price),
        stock: parseInt(form.stock, 10),
      };
      if (editing) {
        if (!body.sku) {
          toast.error("SKU is required when editing");
          setSaving(false);
          return;
        }
        await api.patch(`/api/products/${editing.id}`, body);
        toast.success("Product updated");
      } else {
        const { data } = await api.post("/api/products", body);
        toast.success(data?.sku ? `Product created · SKU ${data.sku}` : "Product created");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    await api.delete(`/api/products/${p.id}`);
    toast.success("Deleted");
    load();
  };

  const onPickImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image too large (max 10 MB)");
      return;
    }
    setUploading(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await api.post("/api/upload/image", body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-12">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-6">
        <div>
          <div className="font-mono text-xs tracking-[0.3em] text-[color:var(--mecha-red)]">
            // INVENTORY
          </div>
          <h1 className="font-display text-3xl sm:text-5xl mt-3 leading-none">PRODUCTS</h1>
        </div>
        <button
          data-testid="new-product-btn"
          onClick={openNew}
          className="btn-brutal bg-[color:var(--mecha-red)] text-white px-6 h-12 font-bold tracking-widest uppercase text-sm flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          New Product
        </button>
      </div>

      {products.length === 0 ? (
        <div className="border border-zinc-800 p-16 text-center">
          <Package className="h-10 w-10 mx-auto text-zinc-600 mb-4" />
          <div className="text-zinc-400 mb-4">No products yet</div>
          <button
            onClick={openNew}
            className="btn-brutal bg-[color:var(--mecha-red)] text-white px-6 h-11 text-sm font-mono tracking-widest uppercase"
          >
            Add your first product
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {products.map((p) => (
            <div
              key={p.id}
              className="card-brutal hoverable-card overflow-hidden"
              data-testid={`product-card-${p.sku}`}
            >
              <div className="aspect-square bg-zinc-900">
                {p.image_url ? (
                  <img src={mediaUrl(p.image_url)} alt={p.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-zinc-700 font-display text-6xl">
                    {p.name.charAt(0)}
                  </div>
                )}
              </div>
              <div className="p-4">
                <div className="flex justify-between font-mono text-[10px] tracking-widest text-zinc-500">
                  <span>{p.sku}</span>
                  <span className="uppercase">{p.category}</span>
                </div>
                <div className="text-sm font-medium mt-1 line-clamp-2 min-h-[2.5rem]">
                  {p.name}
                </div>
                <div className="flex items-end justify-between mt-3">
                  <div className="font-mono font-bold text-[color:var(--mecha-red)]">
                    {fmtINR(p.price)}
                  </div>
                  <div
                    className={`font-mono text-[10px] tracking-widest ${
                      p.stock <= 3 ? "text-[color:var(--warn)]" : "text-zinc-400"
                    }`}
                  >
                    STOCK: {p.stock}
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <button
                    data-testid={`edit-${p.sku}`}
                    onClick={() => openEdit(p)}
                    className="flex-1 h-9 border border-zinc-800 hover:border-white text-xs font-mono tracking-widest flex items-center justify-center gap-2"
                  >
                    <Pencil className="h-3 w-3" /> EDIT
                  </button>
                  <button
                    data-testid={`delete-${p.sku}`}
                    onClick={() => remove(p)}
                    className="h-9 w-9 border border-zinc-800 hover:border-[color:var(--mecha-red)] hover:text-[color:var(--mecha-red)] flex items-center justify-center"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {open && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="card-brutal w-full max-w-lg">
            <div className="flex items-center justify-between p-5 border-b border-zinc-800">
              <div>
                <div className="font-mono text-[10px] tracking-[0.3em] text-[color:var(--mecha-red)]">
                  // {editing ? "EDIT" : "NEW"}
                </div>
                <h2 className="font-display text-2xl mt-1">
                  {editing ? "UPDATE PRODUCT" : "NEW PRODUCT"}
                </h2>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="text-zinc-500 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={save} className="p-5 space-y-4">
              <Field label="NAME" testid="prod-name">
                <input
                  required
                  data-testid="prod-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className={inputCls}
                />
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label={editing ? "SKU" : "SKU (OPTIONAL)"}>
                  <input
                    required={!!editing}
                    data-testid="prod-sku"
                    value={form.sku}
                    onChange={(e) => setForm({ ...form, sku: e.target.value.toUpperCase() })}
                    placeholder={editing ? "" : "Auto if blank"}
                    className={inputCls}
                  />
                </Field>
                <Field label="CATEGORY">
                  <select
                    data-testid="prod-category"
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className={inputCls}
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>
                        {c.toUpperCase()}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              {!editing && (
                <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-500 -mt-2">
                  Leave SKU blank to auto-generate (e.g. FIG-0001 from category).
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <Field label="PRICE (INR)">
                  <input
                    required
                    type="number"
                    step="0.01"
                    data-testid="prod-price"
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                    className={inputCls}
                  />
                </Field>
                <Field label="STOCK">
                  <input
                    required
                    type="number"
                    data-testid="prod-stock"
                    value={form.stock}
                    onChange={(e) => setForm({ ...form, stock: e.target.value })}
                    className={inputCls}
                  />
                </Field>
              </div>

              <Field label="PRODUCT IMAGE">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="hidden"
                  data-testid="prod-image"
                  onChange={onPickImage}
                />
                <div className="flex gap-3 items-stretch">
                  <div className="w-24 h-24 shrink-0 border border-zinc-800 bg-zinc-950 overflow-hidden flex items-center justify-center">
                    {form.image_url ? (
                      <img
                        src={mediaUrl(form.image_url)}
                        alt="Preview"
                        className="w-full h-full object-cover"
                        data-testid="prod-image-preview"
                      />
                    ) : (
                      <ImagePlus className="h-6 w-6 text-zinc-600" />
                    )}
                  </div>
                  <div className="flex-1 flex flex-col gap-2 justify-center">
                    <button
                      type="button"
                      data-testid="prod-image-upload"
                      disabled={uploading}
                      onClick={() => fileRef.current?.click()}
                      className="h-10 border border-zinc-800 hover:border-white text-xs font-mono tracking-widest uppercase flex items-center justify-center gap-2 disabled:opacity-60"
                    >
                      {uploading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : form.image_url ? (
                        "Replace Image"
                      ) : (
                        "Upload Image"
                      )}
                    </button>
                    {form.image_url && (
                      <button
                        type="button"
                        data-testid="prod-image-clear"
                        disabled={uploading}
                        onClick={() => setForm({ ...form, image_url: "" })}
                        className="h-9 text-[11px] font-mono tracking-widest uppercase text-zinc-500 hover:text-[color:var(--mecha-red)]"
                      >
                        Remove
                      </button>
                    )}
                    <div className="font-mono text-[9px] tracking-widest text-zinc-600">
                      JPEG · PNG · WEBP · GIF · MAX 10 MB
                    </div>
                  </div>
                </div>
              </Field>

              <Field label="DESCRIPTION">
                <textarea
                  data-testid="prod-desc"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3}
                  className={inputCls + " resize-none"}
                />
              </Field>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="flex-1 h-11 border border-zinc-800 hover:border-white text-xs font-mono tracking-widest uppercase"
                >
                  Cancel
                </button>
                <button
                  data-testid="prod-save"
                  type="submit"
                  disabled={saving}
                  className="btn-brutal flex-1 h-11 bg-[color:var(--mecha-red)] text-white text-xs font-mono tracking-widest uppercase disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const inputCls =
  "w-full h-11 bg-zinc-950 border border-zinc-800 px-3 text-white text-sm focus:outline-none focus:border-[color:var(--mecha-red)] transition-colors";

function Field({ label, children }) {
  return (
    <div>
      <label className="block font-mono text-[10px] tracking-[0.3em] text-zinc-500 mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}
